import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { ScenarioKeyTable } from '../../components/demo/ScenarioKeyTable';
const STAGE_MS = [1400, 1500, 2000, 1800, 1500, 1600];
function ConsistencyGrid() {
  // 3 models × 10 runs. One amber dot is the 9/10.
  const dots = Array.from({
    length: 30
  }, (_, i) => i);
  return <div data-id="element-793" data-mp="1o00pnnac6amd">
      <div className="grid w-fit grid-cols-10 gap-1.5" data-id="element-794" data-mp="1o1lb8neoaj83">
        {dots.map(i => <span key={i} className={`h-2 w-2 rounded-sm ${i === 7 ? 'bg-signal-amber' : 'bg-brand/70'}`} data-id="element-795" data-mp="1j1yz6hakz90v" />)}
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-white/60" data-id="element-796" data-mp="3ddx5aadlfosj">
        Below 80% consistency, a scenario is rebuilt. This one held at 90.
      </p>
    </div>;
}
export function Pipeline() {
  const {
    mode,
    cvName,
    setPipelineComplete,
    bundle
  } = useDemo();
  const pipelineStages = bundle.pipelineStages;
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const injectRef = useRef<HTMLLIElement>(null);
  const requestedKey = (location.state as {
    openKey?: boolean;
  } | null)?.openKey === true;
  const [keyShown, setKeyShown] = useState(false);
  useEffect(() => {
    if (active >= pipelineStages.length) {
      setPipelineComplete(true);
      return;
    }
    const t = window.setTimeout(() => setActive(a => a + 1), STAGE_MS[active]);
    return () => window.clearTimeout(t);
  }, [active, setPipelineComplete, pipelineStages.length]);

  // Arriving from the configuration screen's "full key" link: open stage ② once it has run.
  useEffect(() => {
    if (!requestedKey || keyShown || active <= 1) return;
    setKeyShown(true);
    setOpen(1);
    window.setTimeout(() => injectRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    }), 80);
  }, [requestedKey, keyShown, active]);
  const complete = active >= pipelineStages.length;
  return <div className="flex min-h-full w-full flex-col bg-ink" data-id="element-797" data-mp="16oec6jjl32kh">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-798" data-mp="16q7ghs6q5apb">
        <LenticMark dark data-id="element-799" data-mp="11qcr5t2lkt6d" />
        <BackLink to="/demo/configure" label="Back to configuration" dark data-id="element-800" data-mp="11ql41kypzyhk" />
      </div>

      <main className="mx-auto w-full max-w-[860px] flex-1 px-8 py-14" data-id="element-801" data-mp="wwqjygf7dhhy">
        <p className="eyebrow text-brand" data-id="element-802" data-mp="rzqgmd9qmsuj">Build &amp; gate · {bundle.label} draft</p>
        <h1 className="mt-4 text-[26px] font-semibold tracking-tight text-white" data-id="element-803" data-mp="n0nd4x9eouoo">
          Built, then made to prove itself.
        </h1>

        <ol className="mt-10 divide-y divide-white/10 border-y border-white/10" data-id="element-804" data-mp="j0d5lg5ytw7p">
          {pipelineStages.map((stage, i) => {
          const state = i < active ? 'done' : i === active ? 'running' : 'pending';
          const isOpen = open === i;
          const stageMeta = i === 0 && mode === 'single' && cvName ? 'selection weighted by claimed experience · content from gated stock' : stage.meta;
          return <li key={stage.name} ref={i === 1 ? injectRef : undefined} data-id="element-805" data-mp="33l0nxwgn6f80" data-mpi={i}>
                <button type="button" disabled={state !== 'done'} onClick={() => setOpen(isOpen ? null : i)} className="grid w-full grid-cols-[28px_150px_minmax(0,1fr)_auto] items-start gap-4 py-5 text-left disabled:cursor-default" data-id="element-806" data-mp="26wuhd405tcr3">
                  <span className="pt-0.5" data-id="element-807" data-mp="2ko7uo07h5edo">
                    {state === 'done' ? <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-brand" data-id="element-808" data-mp="1n70qqt96l3og">
                        <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} data-id="element-809" data-mp="1s5u84wrl9etw" />
                      </span> : state === 'running' ? <span className="flex h-5 w-5 animate-pulse items-center justify-center rounded-sm border border-signal-amber/70 bg-signal-amber/20" data-id="element-810" data-mp="3puzdl6hguj7s">
                        <span className="h-1.5 w-1.5 rounded-full bg-signal-amber" data-id="element-811" data-mp="7i19cndn547t" />
                      </span> : <span className="flex h-5 w-5 items-center justify-center rounded-sm border border-white/15" data-id="element-812" data-mp="hent18u8dp89" />}
                  </span>

                  <span data-id="element-813" data-mp="cfkzolnm57g4">
                    <span className={`block text-[14px] font-medium ${state === 'pending' ? 'text-white/25' : 'text-white'}`} data-id="element-814" data-mp="qfly7ugeo2sf">
                      <span className="mr-2 font-mono-num text-white/40" data-id="element-815" data-mp="2s6figv1c986r">{stage.index}</span>
                      {stage.name}
                    </span>
                    <span className={`mt-0.5 block text-[12px] ${state === 'pending' ? 'text-white/15' : 'text-white/45'}`} data-id="element-816" data-mp="2o76p3ra7ncbq">
                      {stage.detail}
                    </span>
                  </span>

                  <motion.span initial={false} animate={{
                opacity: state === 'pending' ? 0 : 1,
                y: state === 'pending' ? 4 : 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className={`font-mono-num text-[11.5px] leading-relaxed ${state === 'running' ? 'text-signal-amber' : 'text-brand-light'}`} data-id="element-817" data-mp="249jahvwhxdgt">
                    {state === 'running' ? 'running…' : stageMeta}
                  </motion.span>

                  {state === 'done' && <ChevronDownIcon className={`h-4 w-4 text-white/30 transition-transform duration-150 ease-settle ${isOpen ? 'rotate-180' : ''}`} data-id="element-818" data-mp="3fopqowpgofvg" />}
                </button>

                <AnimatePresence initial={false} data-id="element-819" data-mp="3e829ujvxj7nc">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-820" data-mp="39bbrfpwnjzyr">
                      <div className="ml-[44px] mb-6 border-l border-white/10 pl-5" data-id="element-821" data-mp="3s94x2zynunzu">
                        {i === 1 ? <div className="max-w-[760px]" data-id="element-822" data-mp="3n9a4mrz8lqco">
                            <p className="font-mono-num text-[11px] text-white/50" data-id="element-823" data-mp="okbarim2dghl">
                              Written at injection. Sealed from candidates.
                            </p>
                            <div className="mt-4" data-id="element-824" data-mp="jrrxynv7apdu">
                              <ScenarioKeyTable dark data-id="element-825" data-mp="r5st0k1r1x3i" />
                            </div>
                          </div> : i === 2 ? <ConsistencyGrid data-id="element-826" data-mp="112x6zwlfym98" /> : stage.manifest ? <div data-id="element-827" data-mp="1159uep9yxoto">
                            <p className="eyebrow text-white/40" data-id="element-828" data-mp="162a3za6119pj">Manifest</p>
                            <ul className="mt-3 space-y-1.5" data-id="element-829" data-mp="1a3vc6i7m8zte">
                              {stage.manifest.map(m => <li key={m.component} className="flex items-baseline gap-4 font-mono-num text-[11.5px]" data-id="element-830" data-mp="1f1wu2ztb2t3a">
                                  <span className="w-64 text-white/70" data-id="element-831" data-mp="1oryjpw1b4jkb">{m.component}</span>
                                  <span className="text-brand-light" data-id="element-832" data-mp="1tsvzk1yuh1in">{m.hash}</span>
                                </li>)}
                            </ul>
                            <p className="mt-3 text-[12.5px] leading-relaxed text-white/60" data-id="element-833" data-mp="2j3d42w52i729">
                              {stage.drawerCaption}
                            </p>
                          </div> : <p className="max-w-xl text-[12.5px] leading-relaxed text-white/60" data-id="element-834" data-mp="2t1j2v4vkux2w">
                            {stage.drawerCaption}
                          </p>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </ol>

        <AnimatePresence data-id="element-835" data-mp="2a4hx3hu78sxw">
          {complete && <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 border-l-2 border-brand pl-6" data-id="element-836" data-mp="2a59ffqyscu68">
              <h2 className="text-[20px] font-semibold tracking-tight text-white" data-id="element-837" data-mp="1cnj7863rltlg">
                Generated, but governed.
              </h2>
              <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-white/60" data-id="element-838" data-mp="3t24gbc0mj6jz">
                Every check you just watched runs on every assessment, every time — and re-runs
                against every new frontier model release.
              </p>
              <button type="button" onClick={() => navigate('/demo/instrument')} className="group mt-7 inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-839" data-mp="3jcukpm6kn5r4">
                Review the instrument
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-840" data-mp="6u4rtydamuek" />
              </button>
            </motion.div>}
        </AnimatePresence>

        <p className="mt-16 font-mono-num text-[11px] text-white/30" data-id="element-841" data-mp="2rlwwaub8m7bg">
          Demo build · pre-authored pipeline output · production runs live gates
        </p>
      </main>

      <DemoFooter dark data-id="element-842" data-mp="1bzrzoo7x0k9i" />
    </div>;
}